import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Send welcome email once per user (after email is confirmed)
      if (event === "SIGNED_IN" && session?.user) {
        const u = session.user;
        const alreadySent = (u.user_metadata as any)?.welcome_email_sent === true;
        if (!alreadySent && u.email_confirmed_at) {
          const username =
            (u.user_metadata as any)?.username ||
            (u.email ? u.email.split("@")[0] : "crafter");
          // Fire-and-forget; don't block auth flow
          setTimeout(() => {
            supabase.functions
              .invoke("send-resend-email", {
                body: {
                  templateName: "welcome",
                  recipientEmail: u.email,
                  templateData: { username, siteUrl: window.location.origin },
                },
              })
              .then(({ error }) => {
                if (error) {
                  console.warn("Welcome email failed:", error.message);
                  return;
                }
                supabase.auth.updateUser({ data: { welcome_email_sent: true } });
              });
          }, 0);
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
